import type { AudioFramePayload } from "./types";
import { lightingPositionKey } from "./types";
import type { CanonicalKey } from "./types";

/**
 * 35 种律动模式（对齐 Razer Chroma / Corsair iCUE / Logitech G）
 */
export type RhythmVisualMode =
  | "columnUp"
  | "leftRight"
  | "centerSpread"
  | "classicWave"
  | "beatSweep"
  | "splitFreq"
  | "ripple"
  | "beatStrobe"
  | "vortex"
  | "danceWithMusic"
  | "beatDrop"
  | "curvatureFreq"
  | "bridgeStream"
  | "mirrorFlip"
  | "neonPulse"
  | "laserScan"
  | "dualWave"
  | "oceanBreath"
  | "starfield"
  | "pianoRoll"
  | "crossFade"
  | "bassTunnel"
  | "zonePulse"
  | "heartPulse"
  | "cupidArrow"
  | "fireFlame"
  | "auroraBelt"
  | "colorWheel"
  | "meteorTrail"
  | "pulseRing"
  | "lightningStrike"
  | "discoSparkle"
  | "fireworkBurst"
  | "gradientBreath"
  | "spiralGalaxy";

export interface BarRhythmMapping {
  keyColors: Record<string, { r: number; g: number; b: number }>;
  columnLevels: number[];
  positionColors: Record<string, { r: number; g: number; b: number }>;
}

export const OFF_COLOR = { r: 12, g: 12, b: 16 };

export function hsvToRgb(h: number, s: number, v: number) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

export function scaleRgb(
  color: { r: number; g: number; b: number },
  factor: number
) {
  return {
    r: Math.round(color.r * factor),
    g: Math.round(color.g * factor),
    b: Math.round(color.b * factor),
  };
}

export function resolveColumnCount(keys: CanonicalKey[]): number {
  let maxCol = 0;
  for (const key of keys) {
    if (key.isGap) continue;
    maxCol = Math.max(maxCol, key.col);
  }
  return Math.max(1, maxCol + 1);
}

export function groupKeysByColumn(
  keys: CanonicalKey[]
): Map<number, CanonicalKey[]> {
  const map = new Map<number, CanonicalKey[]>();
  for (const key of keys) {
    if (key.isGap) continue;
    const list = map.get(key.col) ?? [];
    list.push(key);
    map.set(key.col, list);
  }
  return map;
}

export function groupKeysByRow(
  keys: CanonicalKey[]
): Map<number, CanonicalKey[]> {
  const map = new Map<number, CanonicalKey[]>();
  for (const key of keys) {
    if (key.isGap) continue;
    const list = map.get(key.row) ?? [];
    list.push(key);
    map.set(key.row, list);
  }
  return map;
}

export function computeColumnEnergies(
  frame: AudioFramePayload,
  columnCount: number
): number[] {
  const levels: number[] = [];
  const binsPerColumn = Math.max(
    1,
    Math.floor(frame.spectrum.length / columnCount)
  );
  const beatBoost = frame.beat ? 0.18 : 0;

  for (let col = 0; col < columnCount; col += 1) {
    const start = col * binsPerColumn;
    const end = Math.min(frame.spectrum.length, start + binsPerColumn);
    let sum = 0;
    for (let i = start; i < end; i += 1) {
      sum += frame.spectrum[i] ?? 0;
    }
    const avg = end > start ? sum / (end - start) : 0;
    levels.push(Math.min(1, avg * (0.85 + frame.rms * 0.35) + beatBoost));
  }
  return levels;
}

function toColumnLevels255(levels: number[]): number[] {
  return levels.map((level) => Math.round(level * 255));
}

export function finishMapping(
  keys: CanonicalKey[],
  keyColors: Record<string, { r: number; g: number; b: number }>,
  columnLevels: number[]
): BarRhythmMapping {
  return {
    keyColors,
    columnLevels: toColumnLevels255(columnLevels),
    positionColors: rhythmKeyColorsToPositionMap(keys, keyColors),
  };
}

export function mapLeftRightRhythmFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[],
  columnCount = resolveColumnCount(keys),
  columnLevels = computeColumnEnergies(frame, columnCount)
): BarRhythmMapping {
  const keyColors: Record<string, { r: number; g: number; b: number }> = {};
  const beatBoost = frame.beat ? 0.15 : 0;

  for (const key of keys) {
    if (key.isGap) continue;
    const level = columnLevels[key.col] ?? 0;
    if (level + beatBoost < 0.05) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }
    const hue = (key.col / Math.max(1, columnCount - 1)) * 300;
    const brightness = Math.min(1, 0.15 + level * 0.75 + beatBoost);
    keyColors[key.uuid] = hsvToRgb(hue, 0.92, brightness);
  }

  return finishMapping(keys, keyColors, columnLevels);
}

export function mapColumnUpRhythmFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[],
  columnCount = resolveColumnCount(keys),
  columnLevels = computeColumnEnergies(frame, columnCount)
): BarRhythmMapping {
  const keyColors: Record<string, { r: number; g: number; b: number }> = {};
  const huePhase = (frame.timestamp / 38) % 360;
  const byCol = groupKeysByColumn(keys);

  for (const [col, colKeys] of byCol) {
    const level = columnLevels[col] ?? 0;
    const sorted = colKeys.slice().sort((a, b) => b.row - a.row);
    const activeCount =
      level < 0.04 ? 0 : Math.max(1, Math.round(level * sorted.length));

    sorted.forEach((key, index) => {
      if (index >= activeCount) {
        keyColors[key.uuid] = OFF_COLOR;
        return;
      }
      const isBarTop = index === activeCount - 1;
      const brightness = isBarTop ? 1 : 0.55 + level * 0.35;
      const hue =
        (huePhase + (col / Math.max(1, columnCount - 1)) * 300 + index * 8) %
        360;
      keyColors[key.uuid] = hsvToRgb(
        hue,
        0.9,
        Math.max(0.18, brightness * (0.2 + level * 0.75))
      );
    });
  }

  return finishMapping(keys, keyColors, columnLevels);
}

/** 频谱横柱 — 与 mapColumnUpRhythmFrame 同款逻辑，按行自左向右伸缩 */
export function mapRowRightRhythmFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[],
  columnCount = resolveColumnCount(keys),
  columnLevels = computeColumnEnergies(frame, columnCount)
): BarRhythmMapping {
  const keyColors: Record<string, { r: number; g: number; b: number }> = {};
  const huePhase = (frame.timestamp / 38) % 360;
  const byRow = groupKeysByRow(keys);
  let maxRow = 0;
  for (const key of keys) {
    if (key.isGap) continue;
    maxRow = Math.max(maxRow, key.row);
  }

  for (const [row, rowKeys] of byRow) {
    let levelSum = 0;
    for (const key of rowKeys) {
      levelSum += columnLevels[key.col] ?? 0;
    }
    const level = rowKeys.length > 0 ? levelSum / rowKeys.length : 0;
    const sorted = rowKeys.slice().sort((a, b) => a.col - b.col);
    const activeCount =
      level < 0.04 ? 0 : Math.max(1, Math.round(level * sorted.length));

    sorted.forEach((key, index) => {
      if (index >= activeCount) {
        keyColors[key.uuid] = OFF_COLOR;
        return;
      }
      const isBarEnd = index === activeCount - 1;
      const brightness = isBarEnd ? 1 : 0.55 + level * 0.35;
      const hue =
        (huePhase + (row / Math.max(1, maxRow)) * 300 + index * 8) % 360;
      keyColors[key.uuid] = hsvToRgb(
        hue,
        0.9,
        Math.max(0.18, brightness * (0.2 + level * 0.75))
      );
    });
  }

  return finishMapping(keys, keyColors, columnLevels);
}

export function mapCenterSpreadRhythmFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[],
  columnCount = resolveColumnCount(keys),
  columnLevels = computeColumnEnergies(frame, columnCount)
): BarRhythmMapping {
  const keyColors: Record<string, { r: number; g: number; b: number }> = {};
  const centerCol = (columnCount - 1) / 2;
  const beatBoost = frame.beat ? 0.35 : 0;

  for (const key of keys) {
    if (key.isGap) continue;
    const dist = Math.abs(key.col - centerCol) / Math.max(1, centerCol);
    const spread = Math.max(0, 1 - dist ** 1.35);
    const colEnergy = columnLevels[key.col] ?? 0;
    const centerPulse = frame.beat ? (1 - dist) * beatBoost : 0;
    const brightness = Math.min(
      1,
      colEnergy * spread * 0.95 + centerPulse + frame.rms * 0.08
    );

    if (brightness < 0.06) {
      keyColors[key.uuid] = OFF_COLOR;
      continue;
    }

    const hue = 195 - dist * 95;
    keyColors[key.uuid] = hsvToRgb(hue, 0.82, Math.max(0.2, brightness));
  }

  return finishMapping(keys, keyColors, columnLevels);
}

export function rhythmKeyColorsToPositionMap(
  keys: CanonicalKey[],
  keyColorsByUuid: Record<string, { r: number; g: number; b: number }>
): Record<string, { r: number; g: number; b: number }> {
  const map: Record<string, { r: number; g: number; b: number }> = {};
  for (const key of keys) {
    if (key.isGap) continue;
    const color = keyColorsByUuid[key.uuid];
    if (!color) continue;
    map[lightingPositionKey(key.row, key.col)] = color;
  }
  return map;
}

export function mapBarRhythmFrame(
  frame: AudioFramePayload,
  keys: CanonicalKey[],
  columnCount = resolveColumnCount(keys)
): BarRhythmMapping {
  return mapLeftRightRhythmFrame(frame, keys, columnCount);
}
